import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { isNativePlatform } from '../utils/platform';

export interface PhotoResult {
  dataUrl: string;
  format: string;
}

/**
 * Take a photo using the device camera (mobile) or file picker (web)
 */
export const takePhoto = async (): Promise<PhotoResult | null> => {
  if (!isNativePlatform()) {
    // On web, return null to trigger file input
    return null;
  }

  try {
    const image = await Camera.getPhoto({
      quality: 90,
      allowEditing: true,
      resultType: CameraResultType.DataUrl,
      source: CameraSource.Camera,
    });

    if (!image.dataUrl) {
      throw new Error('No image data received');
    }

    return {
      dataUrl: image.dataUrl,
      format: image.format || 'jpeg',
    };
  } catch (error) {
    console.error('Error taking photo:', error);
    return null;
  }
};

/**
 * Pick a photo from the device gallery (mobile) or file picker (web)
 */
export const pickPhoto = async (): Promise<PhotoResult | null> => {
  if (!isNativePlatform()) {
    // On web, return null to trigger file input
    return null;
  }

  try {
    const image = await Camera.getPhoto({
      quality: 90,
      allowEditing: true,
      resultType: CameraResultType.DataUrl,
      source: CameraSource.Photos,
    });

    if (!image.dataUrl) {
      throw new Error('No image data received');
    }

    return {
      dataUrl: image.dataUrl,
      format: image.format || 'jpeg',
    };
  } catch (error) {
    console.error('Error picking photo:', error);
    return null;
  }
};

/**
 * Request camera permissions (mobile only)
 */
export const requestCameraPermissions = async (): Promise<boolean> => {
  if (!isNativePlatform()) {
    return true; // Web doesn't need explicit permissions
  }

  try {
    const permissions = await Camera.requestPermissions({
      permissions: ['camera', 'photos'],
    });

    return permissions.camera === 'granted' && permissions.photos === 'granted';
  } catch (error) {
    console.error('Error requesting camera permissions:', error);
    return false;
  }
};

/**
 * Check if camera permissions are granted
 */
export const checkCameraPermissions = async (): Promise<boolean> => {
  if (!isNativePlatform()) {
    return true;
  }

  try {
    const permissions = await Camera.checkPermissions();
    return permissions.camera === 'granted' && permissions.photos === 'granted';
  } catch (error) {
    console.error('Error checking camera permissions:', error);
    return false;
  }
};

/**
 * Convert a data URL to a Blob for upload
 */
export const dataUrlToBlob = (dataUrl: string): Blob => {
  const arr = dataUrl.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new Blob([u8arr], { type: mime });
};

/**
 * Convert a File to a data URL
 */
export const fileToDataUrl = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};
